import { useState, useCallback, useEffect, useRef } from 'react';
import { AutomationBlueprintV2, AutomationCommunicationEvent, AutomationCommunicationMetadata } from '../types';

interface UseAutomationCommunicationStandaloneOptions {
  projectId?: number;
  onBlueprintReceived?: (blueprint: AutomationBlueprintV2) => void;
  onBlueprintSent?: (blueprint: AutomationBlueprintV2) => void;
}

export const useAutomationCommunicationStandalone = (
  options: UseAutomationCommunicationStandaloneOptions = {}
) => {
  const { projectId, onBlueprintReceived, onBlueprintSent } = options;

  const [currentBlueprint, setCurrentBlueprint] = useState<AutomationBlueprintV2 | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const eventListeners = useRef<Set<(event: AutomationCommunicationEvent) => void>>(new Set());

  // Event emitter for blueprint events
  const emitBlueprintEvent = useCallback((event: AutomationCommunicationEvent) => {
    eventListeners.current.forEach((listener) => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in blueprint event listener:', error);
      }
    });
  }, []);

  // Send blueprint to AVA via postMessage
  const sendBlueprintToAVA = useCallback((blueprint: AutomationBlueprintV2, metadata?: AutomationCommunicationMetadata) => {
    const event: AutomationCommunicationEvent = {
      type: 'BLUEPRINT_UPDATED',
      payload: {
        blueprint,
        metadata: {
          source: 'AUTOMATION_BUILDER',
          timestamp: Date.now(),
          projectId,
          ...metadata,
        },
      },
    };

    // Send to AVA iframe
    const avaIframe = document.querySelector('iframe[title="AVA Component"]') as HTMLIFrameElement;
    if (avaIframe?.contentWindow) {
      avaIframe.contentWindow.postMessage({
        type: 'AUTOMATION_BLUEPRINT',
        payload: event,
      }, '*');
    }

    // Emit locally
    emitBlueprintEvent(event);
    setCurrentBlueprint(blueprint);
    onBlueprintSent?.(blueprint);
  }, [projectId, emitBlueprintEvent, onBlueprintSent]);

  // Request blueprint from AVA
  const requestBlueprintFromAVA = useCallback((sessionId?: string) => {
    console.log('requestBlueprintFromAVA: Requesting blueprint from AVA');
    const event: AutomationCommunicationEvent = {
      type: 'BLUEPRINT_REQUESTED',
      payload: {
        metadata: {
          source: 'AUTOMATION_BUILDER',
          timestamp: Date.now(),
          sessionId,
          projectId,
        },
      },
    };

    // Send request to AVA iframe
    const avaIframe = document.querySelector('iframe[title="AVA Component"]') as HTMLIFrameElement;
    if (avaIframe?.contentWindow) {
      avaIframe.contentWindow.postMessage({
        type: 'REQUEST_BLUEPRINT',
        payload: event,
      }, '*');
    }

    emitBlueprintEvent(event);
  }, [projectId, emitBlueprintEvent]);

  // Import blueprint from AVA
  const importBlueprintFromAVA = useCallback((blueprint: AutomationBlueprintV2) => {
    const event: AutomationCommunicationEvent = {
      type: 'BLUEPRINT_IMPORTED',
      payload: {
        blueprint,
        metadata: {
          source: 'AVA',
          timestamp: Date.now(),
          projectId,
        },
      },
    };

    setCurrentBlueprint(blueprint);
    emitBlueprintEvent(event);
    onBlueprintReceived?.(blueprint);
  }, [projectId, emitBlueprintEvent, onBlueprintReceived]);

  // Listen for blueprint events
  const onBlueprintEvent = useCallback((callback: (event: AutomationCommunicationEvent) => void) => {
    eventListeners.current.add(callback);

    // Return cleanup function
    return () => {
      eventListeners.current.delete(callback);
    };
  }, []);

  // Listen for messages from AVA iframe
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify origin for security (in production, check specific domain)
      if (event.origin !== window.location.origin && !event.origin.includes('localhost')) {
        return;
      }

      const { type, payload } = event.data;

      switch (type) {
        case 'AVA_BLUEPRINT_CREATED':
        case 'AVA_BLUEPRINT_UPDATED':
          if (payload?.blueprint) {
            importBlueprintFromAVA(payload.blueprint);
          }
          break;

        case 'AVA_CONNECTED':
          setIsConnected(true);
          break;

        case 'AVA_DISCONNECTED':
          setIsConnected(false);
          break;

        case 'AVA_BLUEPRINT_REQUEST':
          // AVA is requesting current blueprint
          if (currentBlueprint) {
            sendBlueprintToAVA(currentBlueprint);
          }
          break;
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [currentBlueprint, importBlueprintFromAVA, sendBlueprintToAVA]);

  return {
    sendBlueprintToAVA,
    requestBlueprintFromAVA,
    importBlueprintFromAVA,
    onBlueprintEvent,
    currentBlueprint,
    isConnected,
  };
};
